/**
 * ExportPDFModal - Export tournament to PDF
 */
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTournament } from '../../contexts/TournamentContext';
import firebaseSyncService from '../../services/firebaseSync';
import pdfExportService from '../../services/pdfExport';
import Alert from '../common/Alert';

const ExportPDFModal = ({ isOpen, onClose }) => {
  const [includeRanking, setIncludeRanking] = useState(true);
  const [includeSchedule, setIncludeSchedule] = useState(true);
  const [includeResults, setIncludeResults] = useState(true);
  const [exporting, setExporting] = useState(false);

  const { isSignedIn } = useAuth();
  const { showSuccess, showError } = useToast();
  const { exportState } = useTournament();

  if (!isOpen) return null;

  const tournamentId = firebaseSyncService.currentTournamentId;
  const hasActiveTournament = isSignedIn() && tournamentId;

  const handleExport = async () => {
    if (!hasActiveTournament) {
      showError('Aucun tournoi actif');
      return;
    }

    setExporting(true);

    try {
      // Get current tournament state
      const state = exportState();

      // Prepare tournament data for PDF
      const tournamentData = {
        name: firebaseSyncService.currentTournamentName || 'Tournoi sans nom',
        state: state
      };

      // Generate PDF
      pdfExportService.generateTournamentPDF(tournamentData, {
        includeRanking,
        includeSchedule,
        includeResults
      });

      showSuccess('PDF téléchargé avec succès');
      onClose();
    } catch (error) {
      console.error('Export PDF error:', error);
      showError('Erreur lors de l\'export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ zIndex: 1050 }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-file-pdf me-2"></i>
                Exporter en PDF
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                disabled={exporting}
              ></button>
            </div>

            <div className="modal-body">
              {!hasActiveTournament ? (
                <Alert variant="warning">
                  <h6 className="alert-heading">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Aucun tournoi actif
                  </h6>
                  <p className="mb-0">
                    Vous devez être connecté et avoir un tournoi en cours pour exporter en PDF.
                  </p>
                </Alert>
              ) : (
                <>
                  <p className="text-muted mb-3">
                    Sélectionnez les sections à inclure dans le PDF :
                  </p>

                  {/* Options */}
                  <div className="mb-3">
                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="include-ranking"
                        checked={includeRanking}
                        onChange={(e) => setIncludeRanking(e.target.checked)}
                        disabled={exporting}
                      />
                      <label className="form-check-label" htmlFor="include-ranking">
                        <i className="bi bi-trophy-fill text-warning me-2"></i>
                        <strong>Classement</strong>
                        <small className="d-block text-muted ms-4">
                          Tableau complet avec tous les joueurs
                        </small>
                      </label>
                    </div>

                    <div className="form-check mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="include-schedule"
                        checked={includeSchedule}
                        onChange={(e) => setIncludeSchedule(e.target.checked)}
                        disabled={exporting}
                      />
                      <label className="form-check-label" htmlFor="include-schedule">
                        <i className="bi bi-calendar3 text-primary me-2"></i>
                        <strong>Planning</strong>
                        <small className="d-block text-muted ms-4">
                          Tous les tours avec matchs
                        </small>
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="include-results"
                        checked={includeResults}
                        onChange={(e) => setIncludeResults(e.target.checked)}
                        disabled={exporting}
                      />
                      <label className="form-check-label" htmlFor="include-results">
                        <i className="bi bi-clipboard-check text-success me-2"></i>
                        <strong>Résultats</strong>
                        <small className="d-block text-muted ms-4">
                          Matchs terminés avec scores détaillés
                        </small>
                      </label>
                    </div>
                  </div>

                  {/* Info Box */}
                  <Alert variant="info" className="mb-0">
                    <h6 className="small mb-2">
                      <i className="bi bi-info-circle me-2"></i>
                      Informations
                    </h6>
                    <ul className="small mb-0 ps-3">
                      <li>Le PDF sera téléchargé automatiquement</li>
                      <li>Format A4, optimisé pour l'impression</li>
                      <li>Inclut le nom du tournoi et la date</li>
                    </ul>
                  </Alert>
                </>
              )}
            </div>

            <div className="modal-footer">
              {hasActiveTournament && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleExport}
                  disabled={exporting || (!includeRanking && !includeSchedule && !includeResults)}
                >
                  {exporting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Génération...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-download me-2"></i>
                      Télécharger PDF
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={exporting}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExportPDFModal;
